import { updateState } from '@angular-architects/ngrx-toolkit';
import {
    ConversationalChatConnectionError,
    ConversationalChatVisibility,
    PendingMessage,
    Platform
} from '@klant/conversational-connector/klant-conversational-connector.model';
import { ChatMessageModel } from '@klant/conversational-connector/models/ChatMessage.model';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';

import { computed, inject, Injector, runInInjectionContext } from '@angular/core';

import { ApError } from '@ap/error';
import { ApPersistenceService } from '@ap/persistence';

import { withDebugDevTools } from '../../../debug/devtools.features';
import { SessionStorageService } from '../../session-storage/session-storage.service';
import { ChatSessionStore } from '../chat-session-store/chat-session.store';

type ConversationalChatState = {
    chatFullyLoaded: boolean;
    chatMessages: ChatMessageModel[];
    chatVisibility: ConversationalChatVisibility;
    connectionError: string | undefined;
    conversation: ConversationModel | null;
    faqMessage: string | undefined;
    previousFaqQuestion: string | undefined;
    idleTimerActive: boolean;
    pendingMessages: PendingMessage[];
    platform: Platform | null;
    showLiveChatFaqPopUp: boolean;
    takeoverError: ApError;
    unreadChatMessages: ChatMessageModel[];
    unreadChatMessagesCount: number;
};

const initialState: ConversationalChatState = {
    chatFullyLoaded: false,
    chatMessages: [],
    chatVisibility: ConversationalChatVisibility.INVISIBLE,
    connectionError: undefined,
    conversation: null,
    faqMessage: undefined,
    previousFaqQuestion: undefined,
    idleTimerActive: false,
    pendingMessages: [],
    platform: undefined as unknown as Platform,
    showLiveChatFaqPopUp: false,
    takeoverError: undefined as unknown as ApError,
    unreadChatMessages: [],
    unreadChatMessagesCount: 0
};

const RETRY_CONNECTION_ERROR = 'retry connection error';
const CONNECTION_ERROR = 'connection error';

export type ConversationalChatStore = InstanceType<typeof ConversationalChatStore>;

export const ConversationalChatStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withDebugDevTools('conversational-chat'),
    withComputed((store, chatSessionStore = inject(ChatSessionStore)) => ({
        showRetryConnectionError: computed(() => !store.takeoverError() && store.connectionError() === RETRY_CONNECTION_ERROR),
        showConnectionError: computed(() => !store.takeoverError() && store.connectionError() === CONNECTION_ERROR),
        showChat: computed(() => !!store.conversation() && !!chatSessionStore.chatSession() && !store.connectionError()),
        unreadMessageCount: computed(() => store.unreadChatMessagesCount() || store.unreadChatMessages().length)
    })),
    withMethods((store) => ({
        setConversation(conversation: ConversationModel): void {
            updateState(store, 'Conversation was initialized', { conversation });
        },
        resetConversation(): void {
            updateState(store, 'Cleared conversation', { conversation: undefined });
        },

        setChatFullyLoaded(chatFullyLoaded: boolean): void {
            updateState(store, `Chat was ${chatFullyLoaded ? 'fully loaded' : 'not fully loaded'}`, { chatFullyLoaded });
        },

        setFaqMessage(faqMessage?: string): void {
            updateState(store, 'FAQ Message was set', { faqMessage });
        },
        setPreviouslySentFaqQuestion(previousFaqQuestion?: string): void {
            updateState(store, 'Previously sent FAQ question was set', { previousFaqQuestion });
        },

        setChatMessages(messages: ChatMessageModel[]): void {
            updateState(store, 'Chat messages were loaded', { chatMessages: messages });
        },
        addChatMessage(message: ChatMessageModel): void {
            updateState(store, 'New chat message was added', {
                chatMessages: [...store.chatMessages(), message]
            });
        },
        setChatVisibility(chatVisibility: ConversationalChatVisibility): void {
            runInInjectionContext(Injector.create({ providers: [ApPersistenceService, SessionStorageService] }), () => {
                inject(SessionStorageService).updateChatVisibility(chatVisibility);
            });
            updateState(store, 'Chat visibility has changed', { chatVisibility });
        },

        updateChatMessage(chatMessage: ChatMessageModel): void {
            const messages = store.chatMessages();
            const index = messages.findIndex((m) => m.transactionId === chatMessage.transactionId);
            if (index !== -1) {
                updateState(store, 'Chat message was updated', {
                    chatMessages: messages.map((msg, i) => (i === index ? chatMessage : msg))
                });
            }
        },

        addPendingMessage(message: PendingMessage): void {
            updateState(store, 'Pending message was added', {
                pendingMessages: [...store.pendingMessages(), message]
            });
        },
        removePendingMessage(transactionId: string): void {
            updateState(store, 'Pending message was removed', {
                pendingMessages: store.pendingMessages().filter((m) => m.transactionId !== transactionId)
            });
        },

        addUnreadMessages(messages: ChatMessageModel[]): void {
            updateState(store, 'Unread messages were added', {
                unreadChatMessages: [...store.unreadChatMessages(), ...messages]
            });
        },
        clearUnreadMessages(): void {
            if (store.unreadChatMessages().length > 0 || store.unreadChatMessagesCount() > 0) {
                updateState(store, 'Unread messages were cleared', { unreadChatMessages: [], unreadChatMessagesCount: 0 });
            }
        },
        setUnreadChatMessagesCount(unreadChatMessagesCount: number): void {
            updateState(store, `Setting unread messages count to ${unreadChatMessagesCount}`, { unreadChatMessagesCount });
        },

        setTakeoverError(takeoverError: ApError): void {
            updateState(store, 'Takeover error was set', { takeoverError, connectionError: undefined });
        },
        setConnectionError(error: ConversationalChatConnectionError | undefined): void {
            updateState(store, 'Connection error was set', { connectionError: error });
        },

        setIdleTimerActive(idleTimerActive: boolean): void {
            updateState(store, 'Idle timer was activated', { idleTimerActive });
        },
        setShowLiveChatFaqPopUp(show: boolean): void {
            updateState(store, 'Live chat FAQ popup visibility was updated', { showLiveChatFaqPopUp: show });
        },

        setPlatform(platform: Platform): void {
            updateState(store, 'Platform was set', { platform });
        },

        clearErrors(): void {
            updateState(store, 'Cleared errors', { takeoverError: undefined, connectionError: undefined });
        }
    })),
    withHooks({
        onInit(store) {
            restoreChatVisibilityFromSession(store);
        }
    })
);

const restoreChatVisibilityFromSession = (store: ConversationalChatStore): void => {
    const chatVisibility = inject(SessionStorageService).getChatVisibility();
    store.setChatVisibility(chatVisibility);
};
